import { T } from "@/i18n";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  commandItemStyle,
} from "./ui/command";
import { Dialog, DialogContent, DialogTrigger } from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

type Option<T> = {
  value: T;
  label: string;
  description?: string;
};

function asString(value: unknown): string {
  return value ? value.toString() : "";
}

export function SelectSearch<T>({
  id,
  options,
  onChange,
  value,
  className,
  placeholder,
  searchNoResults = "common.inputs.select.noResults",
  searchPlaceholder,
  disabled,
  addButton = "common.inputs.select.new",
  createNode,
}: {
  id?: string;
  disabled?: boolean;
  options: Option<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
  className?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  searchNoResults?: string;
  addButton?: string;
  createNode?: React.ReactNode | ((search: string) => React.ReactNode);
}) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const allowCreate = useMemo(() => !!createNode, [createNode]);

  const sortedOptions = useMemo(
    () =>
      options.sort((o1, o2) =>
        o1.label.toLowerCase().localeCompare(o2.label.toLowerCase())
      ),
    [options]
  );

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          disabled={disabled}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between whitespace-nowrap gap-2 w-full",
            className
          )}
        >
          <span className="grid">
            <span className="whitespace-nowrap overflow-hidden text-ellipsis">
              {value !== undefined ? (
                <Label
                  option={
                    options.find((o) => o.value === value) ??
                    ({
                      label: placeholder,
                      value: value,
                      description: undefined,
                    } as Option<T>)
                  }
                  horizontal
                />
              ) : (
                placeholder
              )}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-auto" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command
          className="max-h-96"
          filter={(value, search) => {
            setSearchInput(search);
            const typedValue = value as T;
            const option = options.find(
              (o) =>
                asString(o.value).toLocaleLowerCase() ===
                asString(typedValue).toLowerCase()
            );
            return option?.label.toLowerCase().includes(search.toLowerCase())
              ? 1
              : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandEmpty>
            <T tKey={searchNoResults} />
          </CommandEmpty>
          <CommandGroup className="overflow-y-scroll">
            {sortedOptions.map((option) => (
              <CommandItem
                key={asString(option.value)}
                value={asString(option.value)}
                onSelect={() => {
                  if (value === option.value) {
                    return;
                  }

                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
                <Label option={option} />
              </CommandItem>
            ))}
          </CommandGroup>
          {allowCreate && (
            <CreateButton text={addButton}>
              {typeof createNode === "function"
                ? createNode(searchInput)
                : createNode}
            </CreateButton>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Label<T>({
  option,
  horizontal,
}: {
  option: Option<T>;
  horizontal?: boolean;
}) {
  if (!option.description) return option.label;
  return (
    <div className={cn(horizontal ? "flex items-center gap-1" : "grid")}>
      <span className="font-semibold">{option.label}</span>
      <span className="text-muted-foreground text-xs">
        {option.description}
      </span>
    </div>
  );
}

function CreateButton({
  text,
  children,
}: {
  text: string;
  children?: React.ReactNode;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          className={cn(
            commandItemStyle,
            "w-full cursor-pointer font-bold border-t border-spacing-2 mt-2"
          )}
          type="button"
        >
          <Plus className="w-4 shrink-0 aspect-square mr-2" />
          <T tKey={text} />
        </button>
      </DialogTrigger>
      <DialogContent
        closeId="select-modal-btn"
        className="md:max-w-[620px] max-w-[90dvw] max-h-[60dvh] overflow-y-auto"
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}
